import { Component, ViewChild } from '@angular/core';
import { DailyLogDatePickerComponent } from '../../../shared/components/daily-log-date-picker/daily-log-date-picker';
import { AppConfig } from '../../../../app/app.config';
import { VesselCrewEditor } from '../../../../models/vessel-crew/vessel-crew.models';
import { catchError, debounce, finalize } from 'rxjs/operators';
import { timer } from 'rxjs/observable/timer';
import { LockDayFilters } from '../../../../models/lock-day.models';
import { NavHelperService } from '../../../../services/nav/nav-helper.service';
import { PtLuxonDate } from '../../../../models/projects/date.models';
import { DailyLogService } from '../../../../services/daily-log/daily-log.service';
import { Subject } from 'rxjs/Subject';
import { CompleteDayService } from '../../../../services/complete-day/complete-day.service';
import { PermissionService } from '../../../../_foundation/services/permission/permission.service';
import { ImplementerService } from '../../../../services/implementer/implementer.service';
import { cloneDeep } from 'lodash';
import { PermissionsService } from '../../../../services/permissions/permissions.service';
import {
  ActionType,
  ErrorType,
  PageValidationType,
  ValidationResult
} from '../../../../models/projects/validation.models';
import { LockedDayService } from '../../../../services/locked-day/locked-day.service';
import {
  JackingActivityRobFigureService
} from '../../../../services/jacking-activity/jacking-activity-rob-figure.service';
import { JackingActivityRobFuelFigureEditor } from '../../../../models/projects/jacking-activity.models';
import { VesselService } from '../../../../services/vessel/vessel.service';

@Component({
  selector: 'jacking-rob-fuel-figures-edit',
  templateUrl: 'jacking-rob-fuel-figures-edit.html'
})
export class JackingRobFuelFiguresEditComponent {
  @ViewChild('dailyLogDatePickerComponent') dailyLogDatePickerComponent: DailyLogDatePickerComponent;

  config = AppConfig.settings;
  fuelFigures: JackingActivityRobFuelFigureEditor[] = [];
  $fuelFiguresConfirmedChange: Subject<any> = new Subject<any>();
  debounceActive = false;

  $validationConfirmedChange: Subject<boolean> = new Subject<boolean>();
  validationDebounceActive = false;

  loading: boolean;
  validationLoading: boolean;
  dailyLogDate: PtLuxonDate;
  currentSetupConfigured: boolean;
  firstLoadComplete: boolean;
  dayLockedByDate: boolean;
  dateBeforeInitialSetup: boolean;
  isImplementerActiveForDay: boolean;

  permissions: any;
  lockedDayPermissions: any;
  dayCompleted: boolean;
  validationResult: ValidationResult = new ValidationResult();
  private lockable: any;
  isNonJackingVessel: boolean = false;

  constructor(private navHelperService: NavHelperService,
              private dailyLogService: DailyLogService,
              private completeDayService: CompleteDayService,
              private permissionService: PermissionsService,
              private implementerService: ImplementerService,
              private lockedDayService: LockedDayService,
              private robFuelFigureService: JackingActivityRobFigureService,
              private vesselService: VesselService
  ) {
      this.navHelperService.currentSetupConfigured.subscribe(currentSetupConfigured => {
        this.currentSetupConfigured = currentSetupConfigured;
      });
    }

    async ngOnInit() {
      this.loading = true;
      this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
      this.isNonJackingVessel = await this.vesselService.isCurrentVesselNonJacking();
      await this.ionViewEnter();

      await this.updateDataEntryVariables();

      this.$fuelFiguresConfirmedChange
        .pipe(
          catchError(err => {
            this.debounceActive = false;
            throw (err.error);
          }),
          debounce(() => timer(600)))
        .subscribe(val => {
          // note that resulting editor.date has not been deserialised here, as all we need is the id

          this.robFuelFigureService.saveFuelFigures(this.fuelFigures)
            .subscribe(res => {
              this.debounceActive = false;
              this.validateRobFiguresForCurrentDay();
            });
        });
    }

    async ionViewEnter() {
      this.validationLoading = true;

      this.loadData(true);
      this.setupValidationDebounce();

      this.permissions = await this.permissionService.consumablePermissions();
      this.lockedDayPermissions = await this.permissionService.lockedDayPermissions(this.dailyLogDate);
    }

    // one method for all - that checks dayCompleted, implementer status, etc, and sets canEnterData flag
    async updateDataEntryVariables() {
      await Promise.all([
        this.checkImplementerActive(),
        this.checkDayLocked(),
        this.permissionService.updateLockedDayFunctionalPermissions(this.lockedDayPermissions, this.dailyLogDate)
      ]);

      await this.setCanEnterDataFlag();
    }

    async setCanEnterDataFlag() {
      if (!this.dayCompleted
        && ((this.config.isImplementerEnvironment && this.isImplementerActiveForDay) || (!this.config.isImplementerEnvironment && this.dayLockedByDate))) {
        this.navHelperService.updateCanEnterData(true);
      } else {
        this.navHelperService.updateCanEnterData(false);
      }
    }

    setupValidationDebounce() {
      this.$validationConfirmedChange
        .pipe(
          catchError(err => {
            this.validationDebounceActive = false;
            throw (err.error);
          }),
          debounce(() => timer(500)))
        .subscribe(val => {
          this.validationLoading = true;
        });
    }

    async checkDayLocked() {
      const filters: LockDayFilters = {
        date: this.dailyLogDate.dateTime,
        userId: null,
        history: null
      };

      const lockedSummary = await this.lockedDayService.checkDay(filters);

      this.dayLockedByDate = lockedSummary.lockedByDate;
      this.lockable = lockedSummary.lockable;

      this.completeDayService.checkDay(filters).then(result => {
        this.dayCompleted = result;
      });
    }

    async dayUnlockedForEditing() {
      this.loading = true;
      await this.checkDayLocked();
      this.loading = false;
    }

    async changesCancelled() {
      this.firstLoadComplete = false;
      this.loading = true;
      await this.loadData(true);
    }

    async changesSaved() {
      await this.loadData(false);
    }


  async loadData(refresh: boolean, dateChange?: boolean) {
    if (refresh) {
      this.loading = true;
    }

    // refresh daily log date, if user has changed the date
    if (dateChange) {
      this.firstLoadComplete = false;
      this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
      await this.updateDataEntryVariables();
    }

    await this.checkDayLocked();

    if (refresh) {
      this.validationResult = new ValidationResult();

      this.robFuelFigureService.getRobFuelFiguresForDay(this.dailyLogDate).then(data => {
        this.fuelFigures = data;
        this.firstLoadComplete = true;
        this.loading = false;
        this.validateRobFiguresForCurrentDay();
      });
    } else {
      this.validationLoading = false;
      this.loading = false;
    }
  }

  private async validateRobFiguresForCurrentDay() {
    this.validationResult = new ValidationResult();

    if (this.isNonJackingVessel) {
      this.validationLoading = false;
      return;
    }

    this.robFuelFigureService.validate(cloneDeep(this.fuelFigures)).subscribe(() => {
        this.validationLoading = false;
      },
      error => {
        if (error.status && error.status === 412) {
          this.validationResult.errors = this.validationResult.errors.concat(
            error.error.errors.filter(p =>
              this.validationResult.errors.filter(er =>
                er.message === p.message).length === 0));

          this.validationResult.errors.forEach(error => {
            if (error.pageValidationType == null) {
              error.pageValidationType = PageValidationType.RobFuelFigures;
              error.type = ErrorType.Warning;
              error.actionType = ActionType.RobFuelFigures;
            }
          });

          this.validationResult.isValid = error.error.isValid;
          this.validationLoading = false;
        }
      });
  }

  async sendToDebounce(row: JackingActivityRobFuelFigureEditor) {
    const rowClone = cloneDeep(row) as JackingActivityRobFuelFigureEditor;
    this.$fuelFiguresConfirmedChange.next(rowClone);
    this.debounceActive = true;
  }

  // isImplementerEnvironment variable only tells us if the environment is the implementer, we still need to check if current date falls between
  // snapshotSummary start and end dates, if it does then we know this date is editable
  async checkImplementerActive() {
    this.isImplementerActiveForDay = await this.implementerService.implementerIsActive(this.config.isVesselEnvironment, cloneDeep(this.dailyLogDate));
  }

  dateBeforeInitialSetupChanged($event: boolean) {
    this.dateBeforeInitialSetup = $event;
  }

  goToActiveLogDay() {
    this.dailyLogDatePickerComponent.goToActiveLogDay();
  }

  dateLoadingStarted() {
    this.firstLoadComplete = false;
  }

  dateLoadingFinished() {
    this.firstLoadComplete = true;
  }

  otherPagesNotComplete(): boolean {
    return this.validationResult.errors.some(e => e.pageValidationType != null
      && e.pageValidationType !== PageValidationType.DailyObservations
      && e.pageValidationType !== PageValidationType.FieldPositioning
      && e.pageValidationType !== PageValidationType.VoyageLog);
  }

  onKeypressValidateDecimal(currentValue: number, numberOfDecimals: number, event: KeyboardEvent) {
    // Backspace, Tab, Enter, Esc, End, Home, Right Arrow, Left Arrow,
    // Insert, Delete, Windows Key Left, Windows Key right, NumLock, ScrollLock
    const allowedSpecialKeys: number[] = [8, 9, 13, 37, 35, 36, 37, 39, 45, 46, 91, 92, 144, 145];
    if (allowedSpecialKeys.indexOf(event.which) !== -1) {
      return;
    }

    // Prevent input if we're over decimal places
    if (currentValue != null) {
      const afterDecimal = currentValue.toString().split('.')[1];
      if (afterDecimal != null) {
        if (afterDecimal.length >= numberOfDecimals) {
          event.preventDefault();
        }
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/DPR Pages/jacking-rob-fuel-figures/jacking-rob-fuel-figures-edit/jacking-rob-fuel-figures-edit.ts